<template>
  <div class="app-shell">
    <aside class="sidebar" :class="{ collapsed: sidebarCollapsed, open: mobileNavOpen }">
      <div class="sidebar-header">
        <div class="sidebar-brand">
          <img v-if="!sidebarCollapsed" :src="logoUrl" alt="LYT Accounting" class="logo-img" />
          <div v-if="showSidebarCopy && !sidebarCollapsed" class="brand-copy">
            <span class="brand-name">LYT Accounting</span>
            <span class="brand-subtitle">Finance workspace</span>
          </div>
        </div>

        <button class="sidebar-icon-btn desktop-only" type="button" @click="() => { track('btn:ToggleSidebar', 'click'); sidebarCollapsed = !sidebarCollapsed }">
          <component :is="sidebarCollapsed ? PanelRightOpen : PanelLeftClose" class="h-4 w-4" />
        </button>

        <button class="sidebar-icon-btn mobile-only" type="button" @click="mobileNavOpen = false">
          <X class="h-4 w-4" />
        </button>
      </div>

      <div v-if="showSidebarCopy" class="integration-wrapper">
        <IntegrationDropdown
          :items="auth.integrations"
          :model-value="activeIntegrationKey"
          :metas="PROVIDER_META"
          @select="switchIntegration"
        />
      </div>

      <nav class="sidebar-nav">
        <router-link
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          class="nav-item"
          :class="{ active: isActive(item.to) }"
          :title="sidebarCollapsed ? item.label : ''"
          :data-label="item.label"
          @click="() => { track('nav:' + item.label, 'click'); mobileNavOpen = false }"
        >
          <component :is="iconMap[item.icon]" class="nav-icon" />
          <span v-if="showSidebarCopy" class="nav-copy">
            <span class="nav-label">{{ item.label }}</span>
          </span>
        </router-link>
      </nav>

      <div v-if="showSidebarCopy" class="sidebar-footer">
        <div class="footer-chip">
          <Building2 class="h-4 w-4" />
          <span>{{ auth.storeID || 'Store pending' }}</span>
        </div>
        <div class="footer-chip">
          <ReceiptText class="h-4 w-4" />
          <span>{{ auth.invoiceNumber || 'Invoice pending' }}</span>
        </div>
      </div>
    </aside>

    <div class="workspace">
      <header class="workspace-header">
        <div class="workspace-heading">
          <button class="sidebar-icon-btn mobile-only" type="button" @click="mobileNavOpen = true">
            <Menu class="h-4 w-4" />
          </button>
          <div>
            <div class="workspace-eyebrow">Accounting workspace</div>
            <div class="workspace-title">{{ currentPageTitle }}</div>
          </div>
        </div>

        <div class="workspace-meta">
          <router-link to="/app/tasks" class="header-task-link"><HeaderActiveTaskBadge /></router-link>
          <div v-if="companyName" class="workspace-company">
            <Building2 class="h-4 w-4" />
            <span>{{ companyName }}</span>
          </div>
          <div class="workspace-state" :class="isConnected ? 'state-connected' : 'state-disconnected'">
            <Landmark class="h-4 w-4" />
            <span>{{ currentProvider?.name || 'Provider pending' }}</span>
            <span class="state-divider">•</span>
            <span>{{ isConnected ? 'Connected' : 'Disconnected' }}</span>
          </div>
        </div>
      </header>

      <main class="workspace-main">
        <router-view />
      </main>
    </div>

    <div v-if="mobileNavOpen" class="overlay" @click="mobileNavOpen = false" />
  </div>
</template>
<script setup lang="ts">
import { track } from '@/lib/usageTracker'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import logoUrl from '../../designs/Logo.png'
import {
  BookOpen,
  Building2,
  CreditCard,
  FileText,
  Landmark,
  LayoutDashboard,
  Menu,
  Package,
  PanelLeftClose,
  PanelRightOpen,
  ReceiptText,
  Settings,
  Users,
  X,
} from 'lucide-vue-next'
import IntegrationDropdown from '@/components/IntegrationDropdown.vue'
import HeaderActiveTaskBadge from '@/components/task/HeaderActiveTaskBadge.vue'

const PROVIDER_META: Record<string, { name: string; iconColor: string }> = {
  QUICKBOOKS: { name: 'QuickBooks', iconColor: '#2ca01c' },
  XERO: { name: 'Xero', iconColor: '#13B5EA' },
}

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()

const sidebarCollapsed = ref(false)
const mobileNavOpen = ref(false)

const iconMap: Record<string, typeof LayoutDashboard> = {
  Dashboard: LayoutDashboard,
  Customers: Users,
  Items: Package,
  Invoices: FileText,
  Payments: CreditCard,
  Ledgers: BookOpen,
  Settings: Settings,
}

const navItems: Array<{ to: string; label: string; icon: string }> = [
  { to: '/app/dashboard', label: 'Dashboard', icon: 'Dashboard' },
  { to: '/app/customers', label: 'Customers', icon: 'Customers' },
  { to: '/app/items', label: 'Items', icon: 'Items' },
  { to: '/app/invoices', label: 'Invoices', icon: 'Invoices' },
  { to: '/app/payments', label: 'Payments', icon: 'Payments' },
  { to: '/app/ledgers', label: 'Ledgers', icon: 'Ledgers' },
  { to: '/app/settings', label: 'Settings', icon: 'Settings' },
]

const integrationKeys = computed(() => Object.keys(auth.integrations))
const activeIntegrationKey = computed(() => auth.selectedPackageId?.toUpperCase() || integrationKeys.value[0] || '')
const showSidebarCopy = computed(() => !sidebarCollapsed.value || mobileNavOpen.value)

const currentProvider = computed(() => {
  const key = activeIntegrationKey.value
  if (!key) return null
  const meta = PROVIDER_META[key] || { name: key, iconColor: '#6b7280' }
  return { ...meta, id: key }
})

const isConnected = computed(() => {
  if (!activeIntegrationKey.value) return false
  return auth.integrations[activeIntegrationKey.value]?.status === 'authenticated'
})

const companyName = computed(() => {
  const account = auth.verification?.account as { companyName?: string; alias?: string } | undefined
  return account?.companyName || account?.alias || ''
})

const currentPageTitle = computed(() => {
  const item = navItems.find((entry) => route.path.startsWith(entry.to))
  return item ? item.label : 'LYT Accounting'
})

function switchIntegration(key: string) {
  auth.selectPackage(key)
  mobileNavOpen.value = false
  router.push('/app/dashboard')
}

function isActive(path: string) {
  return route.path.startsWith(path)
}

watch(
  () => route.fullPath,
  () => {
    mobileNavOpen.value = false
  }
)
</script>

<style scoped>
.app-shell {
  min-height: 100vh;
  display: grid;
  grid-template-columns: auto 1fr;
  background:
    radial-gradient(circle at top left, rgba(79, 70, 229, 0.06), transparent 28%),
    linear-gradient(180deg, var(--color-paper) 0%, #fbfaf6 100%);
}

.sidebar {
  width: 250px;
  height: 100vh;
  min-height: 100vh;
  border-right: 1px solid var(--color-border-soft);
  background: rgba(255, 255, 255, 0.78);
  backdrop-filter: blur(16px);
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  align-self: start;
  overflow-y: auto;
  transition: width 0.22s ease, transform 0.22s ease;
  z-index: 30;
}

.sidebar.collapsed {
  width: 72px;
}

.sidebar-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.sidebar-brand {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.logo-img {
  width: 1.75rem;
  height: 1.75rem;
  object-fit: contain;
  flex-shrink: 0;
}

.brand-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.brand-name {
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--color-ink);
}

.brand-subtitle {
  font-size: 0.7rem;
  color: var(--color-ink-soft);
}

.sidebar-icon-btn {
  width: 2rem;
  height: 2rem;
  border: 1px solid var(--color-border-soft);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-ink-soft);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.workspace-state {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border-radius: var(--radius-sm);
  padding: 0.18rem 0.45rem;
  font-size: 0.68rem;
  font-weight: 600;
}

.state-connected {
  background: rgba(16, 185, 129, 0.1);
  color: #0f8f69;
}

.state-disconnected {
  background: rgba(239, 68, 68, 0.09);
  color: #cc3a3a;
}

.sidebar-nav {
  flex: 1;
  margin-top: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.6rem 0.7rem;
  border-radius: var(--radius-sm);
  color: var(--color-ink-soft);
  text-decoration: none;
  position: relative;
}

.nav-item::before {
  content: '';
  position: absolute;
  left: 0.2rem;
  top: 0.35rem;
  bottom: 0.35rem;
  width: 3px;
  border-radius: 999px;
  background: transparent;
}

.nav-item:hover {
  background: rgba(79, 70, 229, 0.05);
  color: var(--color-ink);
}

.nav-item.active {
  background: rgba(79, 70, 229, 0.08);
  color: var(--color-ink);
}

.nav-item.active::before {
  background: var(--color-primary);
}

.nav-icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

.nav-copy {
  min-width: 0;
}

.nav-label {
  font-size: 0.85rem;
  font-weight: 600;
}

.sidebar-footer {
  padding-top: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.footer-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border-soft);
  background: rgba(255, 255, 255, 0.9);
  padding: 0.35rem 0.55rem;
  font-size: 0.7rem;
  color: var(--color-ink-soft);
}

.workspace {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.workspace-header {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.8rem 1rem;
  border-bottom: 1px solid var(--color-border-soft);
  background: rgba(252, 251, 247, 0.82);
  backdrop-filter: blur(16px);
}

.workspace-heading,
.workspace-meta,
.workspace-company {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.workspace-eyebrow {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-ink-soft);
}

.workspace-title {
  font-size: 1.08rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--color-ink);
}

.workspace-company {
  color: var(--color-ink-soft);
  font-size: 0.78rem;
}

.state-divider {
  opacity: 0.55;
}

.workspace-main {
  padding: 1rem;
  flex: 1;
}

.overlay {
  position: fixed;
  inset: 0;
  background: rgba(21, 32, 51, 0.16);
  z-index: 25;
}

.mobile-only {
  display: none;
}

@media (max-width: 960px) {
  .app-shell {
    grid-template-columns: 1fr;
  }

  .sidebar {
    position: fixed;
    inset: 0 auto 0 0;
    transform: translateX(-100%);
    width: min(84vw, 280px);
    box-shadow: 0 18px 42px rgba(21, 32, 51, 0.18);
  }

  .sidebar.open {
    transform: translateX(0);
  }

  .sidebar.collapsed {
    width: min(88vw, 320px);
  }

  .desktop-only {
    display: none;
  }

  .mobile-only {
    display: inline-flex;
  }

  .workspace-header {
    padding-inline: 0.75rem;
    flex-direction: column;
    align-items: stretch;
  }

  .workspace-meta {
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .workspace-main {
    padding: 0.75rem;
  }
  .header-task-link {
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    color: inherit;
    border-radius: var(--radius-sm);
  }

  .header-task-link:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-primary);
  }

  .header-task-link:hover {
    color: var(--color-primary);
  }
}
</style>
